/**
 * Artifact hooks (task 828) — the files a collaboration produced.
 *
 * Report: `project/reports/innovate-service-collaboration-artifact-outputs.md`.
 *
 * Three reads and two writes, all through the standard service layer:
 *
 *   1. The **listing**, which is both lenses over one store — account-wide, or
 *      narrowed to one resource by `resourceId`. Every row already carries its
 *      latest version's metadata, so a list view costs exactly one request.
 *   2. One **artifact**, for a surface that has only its id.
 *   3. A **version list**, which the service does not have an endpoint for and
 *      this composes: versions are numbered 1…`currentVersionNumber`, so the
 *      numbers are known without asking, and each one's metadata is a read of
 *      its own. Fetched only while a history panel is open, because that is the
 *      only place the whole list is wanted.
 *   4. **Download** — a blob read, then a save; the one call here that does not
 *      answer JSON.
 *   5. **Delete** — soft, server-side; the artifact leaves every listing.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { artifactsApi } from '@/services/api';
import { saveBlobAsFile } from '@/utils/downloadFile';
import type { ArtifactFilters, ArtifactVersionDetail } from '@/types/artifacts';

export const ARTIFACTS_QUERY_KEY = ['artifacts'] as const;

export const artifactsQueryKey = (filters?: ArtifactFilters) =>
  ['artifacts', filters?.resourceId ?? null, filters?.kind ?? null] as const;

export const artifactQueryKey = (artifactId: string | undefined) =>
  ['artifact', artifactId] as const;

export const artifactVersionQueryKey = (artifactId: string | undefined, versionNumber: number) =>
  ['artifact-version', artifactId, versionNumber] as const;

/** The account's artifacts, newest first — or one resource's, with `resourceId`. */
export const useArtifacts = (filters?: ArtifactFilters, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: artifactsQueryKey(filters),
    queryFn: () => artifactsApi.getAll(filters).then((res) => res.data.data),
    enabled: options?.enabled !== false,
  });

export const useArtifact = (artifactId: string | undefined) =>
  useQuery({
    queryKey: artifactQueryKey(artifactId),
    queryFn: () => artifactsApi.getById(artifactId!).then((res) => res.data.data),
    enabled: !!artifactId,
  });

/**
 * Every version of one artifact, newest first.
 *
 * **Composed, not fetched.** The service exposes one version at a time (task
 * 827 shipped no list-versions route), but it does not have to be asked which
 * versions exist: they are 1…`currentVersionNumber` by construction, so the
 * numbers come from the artifact the caller already holds and only the metadata
 * is read. `enabled` matters more than usual here — this is N requests, and it
 * should cost nothing until a reader opens the history.
 */
export const useArtifactVersions = (
  artifactId: string | undefined,
  currentVersionNumber: number | undefined,
  options?: { enabled?: boolean }
) => {
  const count = Math.max(0, currentVersionNumber ?? 0);
  const enabled = options?.enabled !== false && !!artifactId && count > 0;

  const results = useQueries({
    queries: Array.from({ length: count }, (_unused, index) => {
      const versionNumber = count - index; // newest first
      return {
        queryKey: artifactVersionQueryKey(artifactId, versionNumber),
        queryFn: () =>
          artifactsApi.getVersion(artifactId!, versionNumber).then((res) => res.data.data),
        enabled,
      };
    }),
  });

  return {
    versions: results
      .map((result) => result.data)
      .filter((version): version is ArtifactVersionDetail => !!version),
    isLoading: enabled && results.some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
  };
};

/**
 * Download one version, under the artifact's own filename.
 *
 * The name is passed in rather than read from the `Content-Disposition` header:
 * the caller already has it from the row or the reference it is rendering, and
 * parsing a header to recover what is already in hand would be one more place
 * for the saved filename to go wrong.
 */
export const useDownloadArtifactVersion = () =>
  useMutation({
    mutationFn: async ({
      artifactId,
      versionNumber,
      filename,
    }: {
      artifactId: string;
      versionNumber: number;
      filename: string;
    }) => {
      const response = await artifactsApi.getVersionContent(artifactId, versionNumber);
      saveBlobAsFile(response.data, filename);
    },
  });

export const useDeleteArtifact = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (artifactId: string) =>
      artifactsApi.delete(artifactId).then((res) => res.data.data),
    onSuccess: (_data, artifactId) => {
      void queryClient.invalidateQueries({ queryKey: ARTIFACTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: artifactQueryKey(artifactId) });
    },
  });
};
